import { InlineAlertType } from '@varo/graphql-schema-types';
import React, { useState } from 'react';
import { Pressable, StyleSheet, View, ViewStyle } from 'react-native';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

export interface InlineAlertProps {
  /** It act's as a iconKey for Icon component. Leading icon is configurable for all the alert types, also we have default icons set based on the alert type.  */
  iconKeyType?: Maybe<IconKeyType>;
  /** Indicates main title of the alert */
  titleText?: string;
  /** Body text of the alert */
  bodyText?: string;
  /** Type of the alert e.g .info, .success etc */
  alertType: InlineAlertType;
  /** Button prop accept Button component props as an argument. Use `Secondary Alt` button type for InlineAlert*/
  button?: ButtonProps;
  /** OnDismiss handler - Gets called when user dismiss the alert, non-nil onDismiss will show close icon on the alert */
  onDismiss?: () => void;
  /** OnCardPress handler - An optional handler gets called when user taps the whole card, non-nil onCardPress will show the chevron icon on the trailing side */
  onCardPress?: () => void;
  /** Should show an image or not */
  isImageVisible?: boolean;
  /** Accessibility label for the pressable button element */
  accessibilityLabel?: string;
}

interface ComposableStyle {
  container: ViewStyle;
}

interface AlertState {
  default: ComposableStyle;
  focus: ComposableStyle;
  hover: ComposableStyle;
  press: ComposableStyle;
}

interface ComposableStyles {
  INFO: AlertState;
  SUCCESS: AlertState;
  WARNING: AlertState;
  ERROR: AlertState;
  BRAND: AlertState;
}

interface AlignmentStyle {
  container: ViewStyle;
  innerContainer: ViewStyle;
}

const composeAlignmentStyle = (
  onCardPress: InlineAlertProps['onCardPress'],
  button: InlineAlertProps['button'],
): AlignmentStyle => {
  const containerAlignment = onCardPress ? 'center' : 'flex-start';
  const innerContainerAlignment = button ? 'flex-start' : 'center';

  return {
    container: {
      alignItems: containerAlignment,
    },
    innerContainer: {
      alignItems: innerContainerAlignment,
    },
  };
};

export const InlineAlert = ({
  iconKeyType,
  titleText,
  bodyText,
  alertType,
  button,
  onDismiss,
  onCardPress,
  isImageVisible = true,
  accessibilityLabel,
}: InlineAlertProps) => {
  const titleTextLineLimit = 2;
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [showAlert, setShowAlert] = useState(true);

  const computedStyles = composeAlertStyles(
    alertType,
    isHovered,
    isPressed,
    isFocused,
  );

  const alignmentStyles = composeAlignmentStyle(onCardPress, button);

  const handleOnDismissAction = () => {
    setShowAlert(false);
    onDismiss?.();
  };

  if (!showAlert) {
    return null;
  }
  const alertTextStyle = isImageVisible
    ? baseStyles.textContainer
    : baseStyles.textContainerWithoutMargin;

  return (
    <View accessibilityLabel={accessibilityLabel} accessible>
      {onCardPress ? (
        <Pressable
          onPress={onCardPress}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onPressIn={() => setIsPressed(true)}
          onPressOut={() => setIsPressed(false)}
          role="button"
          style={baseStyles.pressableStyles}
        />
      ) : null}
      <View
        pointerEvents="box-none"
        role="alert"
        accessible
        style={[
          baseStyles.container,
          computedStyles.container,
          alignmentStyles.container,
        ]}
      >
        <View
          style={[baseStyles.innerContainer, alignmentStyles.innerContainer]}
        >
          {isImageVisible ? (
            <View>
              <Icon
                iconSize="MD"
                iconKey={iconKeyType ? iconKeyType : alertType}
                iconAccessibilityLabel="info-banner-icon"
              />
            </View>
          ) : null}
          <View style={alertTextStyle}>
            {titleText ? (
              <BodySmBoldText
                style={baseStyles.titleText}
                numberOfLines={titleTextLineLimit}
                testID="alert-title"
              >
                {titleText}
              </BodySmBoldText>
            ) : null}
            {bodyText ? (
              <BodySmText
                style={baseStyles.bodyText}
                testID="alert-body"
                numberOfLines={0}
              >
                {bodyText}
              </BodySmText>
            ) : null}

            {button ? (
              <View style={baseStyles.button}>
                <Button
                  label={button.label}
                  isDisabled={button.isDisabled}
                  onDark={button.onDark}
                  onPress={button.onPress}
                  size={button.size}
                  type={button.type}
                  width={button.width}
                  isLoading={button.isLoading}
                />
              </View>
            ) : null}
          </View>
        </View>

        {onDismiss ? (
          <View style={baseStyles.trailingContainer} testID="alert-dismiss">
            <IconButton
              iconKey="CLOSE"
              iconOnPress={() => handleOnDismissAction()}
              iconButtonAccessibilityLabel="Dismiss alert"
            />
          </View>
        ) : null}

        {onCardPress ? (
          <View style={baseStyles.trailingContainer}>
            <Icon iconKey="CHEVRON_RIGHT" />
          </View>
        ) : null}
      </View>
    </View>
  );
};

const baseStyles = StyleSheet.create({
  bodyText: {
    color: theme.color.text.primary,
  },
  button: {
    alignSelf: 'baseline',
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.status.infoLight,
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    height: 'auto',
    justifyContent: 'space-between',
    padding: theme.spacing.m,
    width: '100%',
  },
  innerContainer: {
    flexBasis: 100,
    flexDirection: 'row',
    flexGrow: 1,
  },
  pressableStyles: {
    flex: 1,
    height: '100%',
    position: 'absolute',
    width: '100%',
    zIndex: 23,
  },
  textContainer: {
    flexDirection: 'column',
    flexShrink: 1,
    marginLeft: theme.spacing.m,
  },
  textContainerWithoutMargin: {
    flexDirection: 'column',
    flexShrink: 1,
  },
  titleText: {
    color: theme.color.text.primary,
  },
  trailingContainer: {
    marginLeft: theme.spacing.m,
  },
});

const inlineAlertStyle: ComposableStyles = {
  INFO: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.infoLight,
        borderColor: theme.color.status.infoLight,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.infoLight,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.infoLightHover,
        borderColor: theme.color.status.infoLightHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.infoLightPressed,
        borderColor: theme.color.status.infoLightPressed,
      },
    }),
  },
  SUCCESS: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positiveLight,
        borderColor: theme.color.status.positiveLight,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positiveLight,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positiveLightHover,
        borderColor: theme.color.status.positiveLightHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.positiveLightPressed,
        borderColor: theme.color.status.positiveLightPressed,
      },
    }),
  },
  WARNING: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warningLight,
        borderColor: theme.color.status.warningLight,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warningLight,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warningLightHover,
        borderColor: theme.color.status.warningLightHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.warningLightPressed,
        borderColor: theme.color.status.warningLightPressed,
      },
    }),
  },
  ERROR: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negativeLight,
        borderColor: theme.color.status.negativeLight,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negativeLight,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negativeLightHover,
        borderColor: theme.color.status.negativeLightHover,
      },
    }),
    press: StyleSheet.create({
      container: {
        backgroundColor: theme.color.status.negativeLightPressed,
        borderColor: theme.color.status.negativeLightPressed,
      },
    }),
  },
  BRAND: {
    default: StyleSheet.create({
      container: {
        backgroundColor: theme.color.expressive.orchid,
        borderColor: theme.color.expressive.orchid,
      },
    }),
    focus: StyleSheet.create({
      container: {
        backgroundColor: theme.color.expressive.orchid,
        borderColor: theme.color.border.focused,
      },
    }),
    hover: StyleSheet.create({
      container: {
        backgroundColor: theme.color.expressive.orchidHover,
        borderColor: theme.color.expressive.orchidHover,
      },
    }),
    press: StyleSheet.create({
      // eslint-disable-next-line react-native/no-unused-styles
      container: {
        backgroundColor: theme.color.expressive.orchidPressed,
        borderColor: theme.color.expressive.orchidPressed,
      },
    }),
  },
};

const composeAlertStyles = (
  type: InlineAlertProps['alertType'],
  isHovered: boolean,
  isPressed: boolean,
  isFocused: boolean,
): ComposableStyle => {
  const getState = () => {
    if (isPressed) {
      return 'press';
    } else if (isHovered) {
      return 'hover';
    } else if (isFocused) {
      return 'focus';
    }

    return 'default';
  };

  return { container: inlineAlertStyle[type][getState()].container };
};

export default InlineAlert;
